import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '../../lib/GlobalVar';
import { ConsultaMonitor } from '../../lib/api';
import Icon from '../Icon';

export default function TIMonitor() {
    const { user } = useContext(GlobalContext);
    const [eventos, setEventos] = useState([]);
    const [stats, setStats] = useState({ total: 0, erros: 0, p50: 0, p95: 0, top: [] });
    const [filtroPath, setFiltroPath] = useState('');
    const [filtroUser, setFiltroUser] = useState('');
    const [somenteErros, setSomenteErros] = useState(false);
    const [pausado, setPausado] = useState(false);
    const [detalhe, setDetalhe] = useState(null);
    const [pagina, setPagina] = useState(1);
    // iter9: visao padrao = agrupada (1 linha por endpoint). Toggle pra "Linhas".
    const [visao, setVisao] = useState('grupo'); // 'grupo' | 'linha'
    const POR_PAGINA = 20;
    const timerRef = useRef(null);

    async function carregar() {
        const r = await ConsultaMonitor({
            solicitante: user?.email,
            limit: 500,
            path: filtroPath || undefined,
            usuario: filtroUser || undefined,
            somenteErros,
        });
        if (r.Error) return;
        setEventos(r.eventos || []);
        setStats(r.stats || {});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        carregar();
        if (!pausado) {
            timerRef.current = setInterval(carregar, 2500);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [filtroPath, filtroUser, somenteErros, pausado]);

    useEffect(() => { setPagina(1); }, [filtroPath, filtroUser, somenteErros, visao]);

    function statusColor(s) {
        if (!s) return 'var(--ink-mute)';
        if (s >= 500) return '#c0392b';
        if (s >= 400) return '#e67e22';
        if (s >= 300) return '#888';
        if (s >= 200) return '#27ae60';
        return 'var(--ink-mute)';
    }

    // iter9: agrupamento por method+path. Cada grupo mantem o ultimo evento como representante.
    const grupos = useMemo(() => {
        const map = new Map();
        for (const e of eventos) {
            const k = `${e.method} ${e.path}`;
            if (!map.has(k)) {
                map.set(k, { chave: k, method: e.method, path: e.path, ultimo: e, todos: [], erros: 0, lats: [] });
            }
            const g = map.get(k);
            g.todos.push(e);
            if (e.erro || e.status >= 400) g.erros++;
            if (typeof e.latency_ms === 'number') g.lats.push(e.latency_ms);
        }
        // p50 por grupo
        return Array.from(map.values()).map(g => {
            const sorted = [...g.lats].sort((a,b)=>a-b);
            const p50 = sorted[Math.floor(sorted.length/2)] || 0;
            return { ...g, count: g.todos.length, p50 };
        }).sort((a,b) => b.count - a.count);
    }, [eventos]);

    // Lista renderizada na tabela (varia conforme visao)
    const lista = visao === 'grupo' ? grupos : eventos;
    const totalPaginas = Math.max(1, Math.ceil(lista.length / POR_PAGINA));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const idxIni = (paginaAtual - 1) * POR_PAGINA;
    const listaPagina = lista.slice(idxIni, idxIni + POR_PAGINA);

    function formataDataHora(ts) {
        if (!ts) return '-';
        const dt = new Date(ts);
        return isNaN(dt) ? String(ts) : dt.toLocaleString('pt-BR', { timeZone:'America/Sao_Paulo' });
    }

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num ti">TI</span> Monitor de Requisições</div>
                    <h1 className="page-title">Monitor de Requisições</h1>
                    <p className="page-sub">
                        Ring buffer das últimas 500 chamadas ao backend. Atualiza a cada 2.5s. Senhas e tokens são mascarados.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn ghost" onClick={() => setPausado(!pausado)}>
                        <Icon name={pausado ? 'play' : 'pause'} size={11}/> {pausado ? 'Retomar' : 'Pausar'}
                    </button>
                    <button className="btn" onClick={carregar}>
                        <Icon name="refresh" size={11}/> Atualizar
                    </button>
                </div>
            </div>

            <div className="kpi-strip" style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:18 }}>
                <div className="card flat" style={{ padding:14 }}>
                    <div className="eyebrow">TOTAL</div>
                    <div style={{ fontSize:28, fontWeight:600 }}>{stats.total}</div>
                </div>
                <div className="card flat" style={{ padding:14 }}>
                    <div className="eyebrow">ERROS</div>
                    <div style={{ fontSize:28, fontWeight:600, color: stats.erros ? '#c0392b' : 'inherit' }}>{stats.erros}</div>
                </div>
                <div className="card flat" style={{ padding:14 }}>
                    <div className="eyebrow">LATÊNCIA P50</div>
                    <div style={{ fontSize:28, fontWeight:600 }}>{stats.p50}<small style={{ fontSize:13, color:'var(--ink-mute)' }}>ms</small></div>
                </div>
                <div className="card flat" style={{ padding:14 }}>
                    <div className="eyebrow">LATÊNCIA P95</div>
                    <div style={{ fontSize:28, fontWeight:600, color: stats.p95 > 500 ? '#e67e22' : 'inherit' }}>{stats.p95}<small style={{ fontSize:13, color:'var(--ink-mute)' }}>ms</small></div>
                </div>
                <div className="card flat" style={{ padding:14 }}>
                    <div className="eyebrow">TOP ENDPOINT</div>
                    <div style={{ fontSize:13, fontWeight:600, fontFamily:'monospace' }}>
                        {(stats.top || [])[0]?.[0]?.replace(/^\//, '') || '—'}
                        <small style={{ fontSize:11, color:'var(--ink-mute)', marginLeft:6 }}>({(stats.top || [])[0]?.[1] || 0})</small>
                    </div>
                </div>
            </div>

            <div className="card flat" style={{ padding:'14px 16px', marginBottom:14, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                <input className="input" placeholder="Filtrar por endpoint (ex: Consulta)" value={filtroPath} onChange={e=>setFiltroPath(e.target.value)} style={{ width:240 }}/>
                <input className="input" placeholder="Filtrar por usuário/email" value={filtroUser} onChange={e=>setFiltroUser(e.target.value)} style={{ width:240 }}/>
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5 }}>
                    <input type="checkbox" checked={somenteErros} onChange={e=>setSomenteErros(e.target.checked)}/>
                    Apenas erros
                </label>
                <div style={{ display:'flex', border:'1px solid var(--line)' }}>
                    <button className={'btn sm ' + (visao==='grupo'?'':'ghost')} onClick={()=>setVisao('grupo')} style={{ borderRadius:0 }}>Agrupado</button>
                    <button className={'btn sm ' + (visao==='linha'?'':'ghost')} onClick={()=>setVisao('linha')} style={{ borderRadius:0 }}>Linhas</button>
                </div>
                <span className="muted" style={{ marginLeft:'auto', fontSize:11.5 }}>
                    {visao==='grupo' ? `${grupos.length} endpoints` : `${eventos.length} eventos`} · {pausado ? 'pausado' : 'live'}
                </span>
            </div>

            <div className="card flat" style={{ padding:0 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                    <thead>
                        <tr style={{ background:'var(--bg-2)', textAlign:'left', borderBottom:'1px solid var(--line)' }}>
                            {visao === 'grupo' ? (
                                <>
                                    <th style={{ padding:'10px 12px' }}>ENDPOINT</th>
                                    <th style={{ padding:'10px 12px', width:90, textAlign:'right' }}>CHAMADAS</th>
                                    <th style={{ padding:'10px 12px', width:80, textAlign:'right' }}>ERROS</th>
                                    <th style={{ padding:'10px 12px', width:90, textAlign:'right' }}>LAT. P50</th>
                                    <th style={{ padding:'10px 12px', width:80 }}>ÚLTIMO</th>
                                    <th style={{ padding:'10px 12px' }}>ÚLTIMA CHAMADA</th>
                                    <th style={{ padding:'10px 12px', width:60 }}>—</th>
                                </>
                            ) : (
                                <>
                                    <th style={{ padding:'10px 12px', width:70 }}>STATUS</th>
                                    <th style={{ padding:'10px 12px', width:220 }}>ENDPOINT</th>
                                    <th style={{ padding:'10px 12px' }}>USUÁRIO</th>
                                    <th style={{ padding:'10px 12px', width:80, textAlign:'right' }}>LATÊNCIA</th>
                                    <th style={{ padding:'10px 12px' }}>ERRO</th>
                                    <th style={{ padding:'10px 12px', width:60 }}>—</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {visao === 'grupo' && listaPagina.map(g => (
                            <tr key={g.chave} style={{ borderBottom:'1px solid var(--line)' }}>
                                <td style={{ padding:'8px 12px', fontFamily:'monospace' }}>{g.method} {g.path}</td>
                                <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:600 }}>{g.count}</td>
                                <td style={{ padding:'8px 12px', textAlign:'right', color: g.erros?'#c0392b':'var(--ink-mute)', fontWeight: g.erros?600:400 }}>{g.erros}</td>
                                <td style={{ padding:'8px 12px', textAlign:'right', color: g.p50>500?'#e67e22':'inherit' }}>{g.p50}ms</td>
                                <td style={{ padding:'8px 12px', fontWeight:600, color:statusColor(g.ultimo.status) }}>{g.ultimo.status}</td>
                                <td style={{ padding:'8px 12px', fontSize:11.5, color:'var(--ink-mute)' }} title={formataDataHora(g.ultimo.ts)}>
                                    {formataDataHora(g.ultimo.ts)}
                                </td>
                                <td style={{ padding:'8px 12px' }}>
                                    <button className="btn ghost sm" onClick={()=>setDetalhe(g.ultimo)}>ver</button>
                                </td>
                            </tr>
                        ))}
                        {visao === 'linha' && listaPagina.map(e => (
                            <tr key={e.id} style={{ borderBottom:'1px solid var(--line)' }}>
                                <td style={{ padding:'8px 12px', fontWeight:600, color:statusColor(e.status) }}>{e.status}</td>
                                <td style={{ padding:'8px 12px', fontFamily:'monospace' }}>
                                    {e.method} {e.path}
                                    {(e.detalhes || []).map((d,i) => (
                                        <span key={i} className="chip" style={{ marginLeft:6, fontSize:9.5, padding:'1px 6px', verticalAlign:'middle' }}>{d.tipo}</span>
                                    ))}
                                </td>
                                <td style={{ padding:'8px 12px', fontSize:11.5 }}>{e.usuario}</td>
                                <td style={{ padding:'8px 12px', textAlign:'right', color: e.latency_ms>500?'#e67e22':'inherit' }}>{e.latency_ms}ms</td>
                                <td style={{ padding:'8px 12px', color:'#c0392b', fontSize:11, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={e.erro}>{e.erro}</td>
                                <td style={{ padding:'8px 12px' }}>
                                    <button className="btn ghost sm" onClick={()=>setDetalhe(e)}>ver</button>
                                </td>
                            </tr>
                        ))}
                        {listaPagina.length === 0 && (
                            <tr><td colSpan={7} style={{ padding:30, textAlign:'center', color:'var(--ink-mute)' }}>Nenhum {visao==='grupo'?'endpoint':'evento'} (ajuste filtros).</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {lista.length > POR_PAGINA && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:14, fontSize:12 }}>
                    <span className="muted">
                        Mostrando <b>{idxIni + 1}</b>–<b>{Math.min(idxIni + POR_PAGINA, lista.length)}</b> de <b>{lista.length}</b>
                    </span>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        <button className="btn ghost sm" disabled={paginaAtual <= 1} onClick={()=>setPagina(1)}>« Primeira</button>
                        <button className="btn ghost sm" disabled={paginaAtual <= 1} onClick={()=>setPagina(paginaAtual - 1)}>‹ Anterior</button>
                        <span className="mono" style={{ padding:'0 10px' }}>Página {paginaAtual} de {totalPaginas}</span>
                        <button className="btn ghost sm" disabled={paginaAtual >= totalPaginas} onClick={()=>setPagina(paginaAtual + 1)}>Próxima ›</button>
                        <button className="btn ghost sm" disabled={paginaAtual >= totalPaginas} onClick={()=>setPagina(totalPaginas)}>Última »</button>
                    </div>
                </div>
            )}

            {detalhe && (
                <div className="modal-mask" onClick={()=>setDetalhe(null)}>
                    <div className="modal" style={{ maxWidth:820 }} onClick={e=>e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="eyebrow">Detalhe da requisição</div>
                            <button className="icon-btn" onClick={()=>setDetalhe(null)}><Icon name="x" size={14}/></button>
                        </div>
                        <div className="modal-body" style={{ fontFamily:'monospace', fontSize:12 }}>
                            <div><b>{detalhe.method} {detalhe.path}</b></div>
                            <div style={{ color:'var(--ink-mute)', marginTop:6 }}>
                                {formataDataHora(detalhe.ts)} · status {detalhe.status} · {detalhe.latency_ms}ms · usuário: {detalhe.usuario}
                            </div>

                            {/* iter9: ultimas 10 chamadas deste mesmo endpoint */}
                            {(() => {
                                const historico = eventos
                                    .filter(x => x.path === detalhe.path && x.method === detalhe.method)
                                    .slice(0, 10);
                                if (historico.length <= 1) return null;
                                return (
                                    <div style={{ marginTop:14 }}>
                                        <div className="eyebrow" style={{ marginBottom:6 }}>ULTIMAS {historico.length} ATUALIZACOES DESTE ENDPOINT</div>
                                        <div style={{ background:'var(--bg-2)', border:'1px solid var(--line)' }}>
                                            {historico.map(h => {
                                                const ativo = h.id === detalhe.id;
                                                return (
                                                    <div key={h.id}
                                                         onClick={() => setDetalhe(h)}
                                                         style={{
                                                             display:'grid',
                                                             gridTemplateColumns:'160px 60px 80px 1fr',
                                                             gap:10,
                                                             padding:'7px 10px',
                                                             borderBottom:'1px solid var(--line)',
                                                             fontSize:11,
                                                             cursor:'pointer',
                                                             background: ativo ? 'var(--nude, #f5e8d8)' : 'transparent'
                                                         }}>
                                                        <span>{formataDataHora(h.ts)}</span>
                                                        <span style={{ fontWeight:600, color:statusColor(h.status) }}>{h.status}</span>
                                                        <span style={{ color: h.latency_ms>500?'#e67e22':'var(--ink-mute)' }}>{h.latency_ms}ms</span>
                                                        <span style={{ color: h.erro?'#c0392b':'var(--ink-mute)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={h.erro || h.usuario}>
                                                            {h.erro ? h.erro : (h.usuario || '-')}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="muted" style={{ fontSize:10.5, marginTop:4 }}>
                                            Clique numa linha para ver os detalhes daquela chamada.
                                        </div>
                                    </div>
                                );
                            })()}

                            {detalhe.erro && (
                                <div style={{ marginTop:10, padding:10, background:'#fdf2f0', border:'1px solid #fadbd8', color:'#c0392b', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
                                    {detalhe.erro}
                                </div>
                            )}
                            {(detalhe.detalhes && detalhe.detalhes.length > 0) && (
                                <div style={{ marginTop:12 }}>
                                    <div className="eyebrow" style={{ marginBottom:6 }}>DETALHES TÉCNICOS ({detalhe.detalhes.length})</div>
                                    {detalhe.detalhes.map((d, i) => (
                                        <div key={i} style={{ background:'var(--bg-2)', padding:10, marginBottom:8, borderLeft:'3px solid #555' }}>
                                            <div style={{ marginBottom:6 }}>
                                                <span className="chip on" style={{ fontSize:10 }}>{d.tipo}</span>
                                                {d.driver && <span style={{ marginLeft:8, fontSize:10.5, color:'var(--ink-mute)' }}>driver={d.driver}</span>}
                                                {d.slug && <span style={{ marginLeft:8, fontSize:10.5, color:'var(--ink-mute)' }}>slug={d.slug}</span>}
                                                {d.conexao && <span style={{ marginLeft:8, fontSize:10.5, color:'var(--ink-mute)' }}>conexao={d.conexao}</span>}
                                            </div>
                                            {d.sql && (
                                                <div style={{ marginTop:6 }}>
                                                    <div style={{ fontSize:10.5, color:'var(--ink-mute)', marginBottom:3 }}>{d.host}{d.port?':'+d.port:''} · db={d.db} · user={d.user}</div>
                                                    <pre style={{ background:'#fafafa', padding:8, fontSize:11, whiteSpace:'pre-wrap', wordBreak:'break-word', borderLeft:'2px solid #4a7' }}>{d.sql}</pre>
                                                    {d.params && d.params.length > 0 && <div style={{ fontSize:10.5, color:'var(--ink-mute)' }}>params: [{d.params.map(p => JSON.stringify(p)).join(', ')}]</div>}
                                                </div>
                                            )}
                                            {d.tipo === 'rest' && (
                                                <div style={{ marginTop:6, fontSize:11, fontFamily:'monospace' }}>
                                                    <div><b>{d.method}</b> {d.url}</div>
                                                    <div style={{ fontSize:10.5, color:'var(--ink-mute)' }}>headers={d.headers_count} · auth={d.auth_tipo} · body_size={d.body_size}</div>
                                                </div>
                                            )}
                                            {d.tipo === 'rest_resp' && (
                                                <div style={{ marginTop:6, fontSize:11 }}>
                                                    <span style={{ fontWeight:600 }}>HTTP {d.http}</span> {d.statusText} · <span style={{ color:'var(--ink-mute)' }}>{d.content_type}</span>
                                                </div>
                                            )}
                                            {d.tipo === 'smb' && (
                                                <div style={{ marginTop:6, fontSize:11, fontFamily:'monospace' }}>
                                                    <div>\\{d.host}\{d.share}\{d.path || ''}</div>
                                                    <div style={{ fontSize:10.5, color:'var(--ink-mute)' }}>{d.dominio}\{d.usuario} · servidor={d.server_nome || d.server_id || '(padrão)'} · {d.smbVersao || 'auto'}</div>
                                                </div>
                                            )}
                                            {(d.tipo === 'resultado' || d.tipo === 'smb_resultado') && (
                                                <div style={{ marginTop:6, fontSize:11 }}>
                                                    {d.rows != null && <span>{d.rows} linhas</span>}
                                                    {d.colunas != null && <span> · {d.colunas} colunas</span>}
                                                    {d.itens != null && <span>{d.itens} itens</span>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* iter9: LOG COMPLETO - JSON inteiro do evento (substitui o bloco PAYLOAD) */}
                            <div style={{ marginTop:12 }}>
                                <div className="eyebrow" style={{ marginBottom:6, display:'flex', alignItems:'center', gap:8 }}>
                                    <span>LOG COMPLETO</span>
                                    <button
                                        type="button"
                                        className="btn ghost sm"
                                        onClick={() => {
                                            try { navigator.clipboard.writeText(JSON.stringify(detalhe, null, 2)); } catch (_) {}
                                        }}
                                        style={{ padding:'1px 8px', fontSize:10 }}
                                    >copiar</button>
                                    <span className="muted" style={{ fontSize:10 }}>JSON cru do evento (senhas/tokens mascarados)</span>
                                </div>
                                <pre style={{ background:'var(--bg-2)', padding:12, fontSize:11, whiteSpace:'pre-wrap', wordBreak:'break-word', maxHeight:420, overflow:'auto', border:'1px solid var(--line)' }}>
                                    {JSON.stringify(detalhe, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
